<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppSidebar from '@/components/AppSidebar.vue'
import MobileTopBar from '@/components/MobileTopBar.vue'
import AiSidebar from '@/components/ai/AiSidebar.vue'
import CommandPalette from '@/components/CommandPalette.vue'
import { provideAppLayout } from '@/composables/useAppLayout'
import { useAiChatStore } from '@/stores/ai-chat'
import { useAuthStore } from '@/stores/auth'
import { Toaster } from '@kyoto2/components/ui/sonner'

const route = useRoute()
const router = useRouter()
const aiChat = useAiChatStore()
const auth = useAuthStore()
const layout = provideAppLayout()
const { pagePaddingClass, pageEndSpacerClass, isFullHeightRoute } = layout
const isPublicPage = computed(() => route.meta.public === true)
/** Login/setup/public dashboards + auth-required interstitials (OAuth consent). */
const isBarePage = computed(() => isPublicPage.value || route.meta.bare === true)
const isFullWidthPage = computed(() => route.meta.fullWidth === true)

onMounted(() => {
  window.addEventListener('keydown', onGlobalKeydown)
})

watch(
  () => auth.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      void aiChat.checkAiStatus()
    } else {
      aiChat.aiEnabled = null
      aiChat.aiUnavailableReason = null
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
})

function onGlobalKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
    e.preventDefault()
    aiChat.toggleSidebar()
  }
}

watch(
  () => route.query.ai,
  async (id) => {
    if (!id || isBarePage.value) return
    const convId = Number(id)
    if (!Number.isFinite(convId)) return
    if (aiChat.isStreaming) return
    if (aiChat.conversationId === convId) {
      aiChat.toggleSidebar(true)
      return
    }
    aiChat.toggleSidebar(true)
    await aiChat.loadConversation(convId)
  },
  { immediate: true },
)

watch(
  () => aiChat.conversationId,
  (id) => {
    if (isBarePage.value) return
    const current = route.query.ai
    if (id ? String(id) === current : !current) return
    const nextQuery = { ...route.query }
    if (id) nextQuery.ai = String(id)
    else delete nextQuery.ai
    // Keep name/params/hash so swapping chats on Data Explorer (or any
    // parameterized route) does not drop path segments like /:connectorId.
    router.replace({
      name: route.name ?? undefined,
      params: route.params,
      query: nextQuery,
      hash: route.hash,
    })
  },
)
</script>

<template>
  <div class="min-h-screen bg-background text-foreground antialiased">
    <Toaster />
    <CommandPalette />
    <template v-if="isBarePage">
      <router-view />
    </template>
    <template v-else>
      <div class="flex flex-col h-dvh lg:h-screen">
        <MobileTopBar />
        <div class="flex flex-1 min-h-0">
          <AppSidebar />
          <main
            class="flex-1 min-w-0"
            :class="isFullHeightRoute ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-auto'"
          >
            <div
              class="flex flex-col"
              :class="[
                isFullWidthPage ? 'w-full max-w-none' : 'mx-auto w-full max-w-6xl',
                pagePaddingClass,
                isFullHeightRoute ? 'min-h-0 flex-1' : 'min-h-full',
              ]"
            >
              <router-view />
              <div class="shrink-0" :class="pageEndSpacerClass" aria-hidden="true" />
            </div>
          </main>
          <AiSidebar />
        </div>
      </div>
    </template>
  </div>
</template>
